/**
 * API endpoints para QR Codes (Catálogo Público)
 * Base URL: /api/public-catalog/
 */

import { GET, POST, PUT, PATCH, DELETE } from "../config";

/**
 * Listar códigos QR
 * @param {number} page - Número de página
 * @param {object} filters - Filtros (catalog, product, is_active)
 * @returns {Promise} Lista de códigos QR
 */
export const list = async (page = 1, filters = {}) => {
  let url = `public_catalog/qr-codes/?page=${page}&page_size=50`;

  if (filters.catalog) {
    url += `&catalog=${filters.catalog}`;
  }

  if (filters.product) {
    url += `&product=${filters.product}`;
  }

  if (filters.is_active !== undefined) {
    url += `&is_active=${filters.is_active}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener un código QR por ID
 * @param {string|number} id - ID del código QR
 * @returns {Promise} Datos del código QR
 */
export const get = async (id) => {
  const response = await GET(`public_catalog/qr-codes/${id}/`);
  return response.data;
};

/**
 * Crear un código QR
 * @param {object} data - Datos del código QR
 * @returns {Promise} Código QR creado
 */
export const create = async (data) => {
  const response = await POST("public_catalog/qr-codes/", data);
  return response.data;
};

/**
 * Generar código QR para un producto
 * @param {number} catalogId - ID del catálogo
 * @param {number} productId - ID del producto
 * @param {object} options - Opciones adicionales (size, include_price, etc.)
 * @returns {Promise} Código QR generado
 */
export const generateForProduct = async (catalogId, productId, options = {}) => {
  const response = await POST("public_catalog/qr-codes/generate/", {
    catalog: catalogId,
    product: productId,
    ...options,
  });
  return response.data;
};

/**
 * Actualizar un código QR
 * @param {string|number} id - ID del código QR
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Código QR actualizado
 */
export const update = async (id, data) => {
  const response = await PUT(`public_catalog/qr-codes/${id}/`, data);
  return response.data;
};

/**
 * Actualizar parcialmente un código QR
 * @param {string|number} id - ID del código QR
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Código QR actualizado
 */
export const partialUpdate = async (id, data) => {
  const response = await PATCH(`public_catalog/qr-codes/${id}/`, data);
  return response.data;
};

/**
 * Eliminar un código QR
 * @param {string|number} id - ID del código QR
 * @returns {Promise}
 */
export const remove = async (id) => {
  const response = await DELETE(`public_catalog/qr-codes/${id}/`);
  return response.data;
};

/**
 * Descargar imagen del código QR
 * @param {string|number} id - ID del código QR
 * @param {string} format - Formato (png, svg, pdf)
 * @returns {Promise} Blob de la imagen
 */
export const download = async (id, format = "png") => {
  const { Axios } = require("../config");
  const token = localStorage.getItem("token");

  try {
    const response = await Axios.get(
      `public_catalog/qr-codes/${id}/download/?format=${format}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
        responseType: "blob",
      }
    );

    return response.data;
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const errorJson = JSON.parse(text);
        throw new Error(errorJson.error || "Error descargando QR");
      } catch {
        throw new Error("Error descargando QR");
      }
    }
    throw new Error(error.message || "Error descargando QR");
  }
};

/**
 * Escanear/Validar un código QR
 * @param {string} code - Contenido del código QR
 * @returns {Promise} Información del producto asociado
 */
export const scan = async (code) => {
  const response = await POST("public_catalog/qr-codes/scan/", { code });
  return response.data;
};

/**
 * Imprimir múltiples códigos QR
 * @param {array} ids - IDs de los códigos QR a imprimir
 * @param {object} options - Opciones de impresión (template, paper_size)
 * @returns {Promise} Blob del PDF generado
 */
export const printBatch = async (ids, options = {}) => {
  const { Axios } = require("../config");
  const token = localStorage.getItem("token");

  const response = await Axios.post(
    "public_catalog/qr-codes/print-batch/",
    {
      ids,
      ...options,
    },
    {
      headers: {
        Authorization: `Token ${token}`,
      },
      responseType: "blob",
    }
  );

  return response.data;
};

/**
 * Regenerar código QR
 * @param {string|number} id - ID del código QR
 * @returns {Promise} Código QR regenerado
 */
export const regenerate = async (id) => {
  const response = await POST(`public_catalog/qr-codes/${id}/regenerate/`);
  return response.data;
};

/**
 * Obtener estadísticas de escaneos
 * @param {number} qrCodeId - ID del código QR
 * @param {object} params - Parámetros (date_from, date_to)
 * @returns {Promise} Estadísticas de escaneos
 */
export const getScanStats = async (qrCodeId, params = {}) => {
  let url = `public_catalog/qr-codes/${qrCodeId}/scan-stats/?`;
  const queryParams = new URLSearchParams();

  if (params.date_from) {
    queryParams.append("date_from", params.date_from);
  }

  if (params.date_to) {
    queryParams.append("date_to", params.date_to);
  }

  url += queryParams.toString();

  const response = await GET(url);
  return response.data;
};

// ==================== EXPORT ====================

const qrCodesApi = {
  list,
  get,
  create,
  generateForProduct,
  update,
  partialUpdate,
  remove,
  download,
  scan,
  printBatch,
  regenerate,
  getScanStats,
};

export default qrCodesApi;
